// Marcações do Espelho ao vivo (lápis, seta, texto, ponteiro): o supervisor desenha na imagem da tela do agente e a
// aba do agente mostra por cima do WootDash. Os dois lados usam ESTE estado e ESTE desenho, então veem o mesmo.
// Coordenadas sempre em proporção da tela do agente (0 a 1) — vale com janelas de tamanhos diferentes.
// Regras: lápis e seta somem 10 s depois de terminados; texto fica até "Limpar"; o ponteiro some 1,5 s parado.

export type MarkKind = 'stroke' | 'arrow' | 'text'
export type Point = [number, number]

export interface Mark {
  id: string
  kind: MarkKind
  color: string
  width: number
  points: Point[]
  text?: string
  done: boolean
  /** Quando terminou (lápis/seta): conta os 10 s a partir daqui. */
  doneAt?: number
}

/** Mensagem que vai pelo hub (MarcarEspelho → MarcacaoEspelho). */
export type MarkMessage =
  | { op: 'add'; mark: Mark }
  | { op: 'append'; id: string; points: Point[]; done?: boolean }
  | { op: 'pointer'; x: number; y: number; color: string }
  | { op: 'undo'; id: string }
  | { op: 'clear' }

export const STROKE_TTL_MS = 10_000
export const FADE_MS = 1_000
export const POINTER_TTL_MS = 1_500
export const MAX_TEXT_LENGTH = 200
export const MARK_COLORS = ['#ef4444', '#f59e0b', '#2563eb'] as const

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0)
const cleanPoints = (points: unknown): Point[] =>
  Array.isArray(points) ? points.slice(0, 2_000).map(p => [clamp01(Number((p as number[])[0])), clamp01(Number((p as number[])[1]))] as Point) : []

/** Estado das marcações de uma tela (o do supervisor e o da aba do agente). */
export class MarkBoard {
  marks = new Map<string, Mark>()
  pointer: { x: number; y: number; color: string; at: number } | null = null

  /** Aplica uma mensagem (validando: vem da rede). Devolve se mudou algo. */
  apply(msg: MarkMessage, now = Date.now()): boolean {
    switch (msg?.op) {
      case 'add': {
        const m = msg.mark
        if (!m?.id || !['stroke', 'arrow', 'text'].includes(m.kind)) return false
        const done = !!m.done
        this.marks.set(m.id, {
          id: String(m.id).slice(0, 40),
          kind: m.kind,
          color: MARK_COLORS.includes(m.color as typeof MARK_COLORS[number]) ? m.color : MARK_COLORS[0],
          width: Math.min(12, Math.max(1, Number(m.width) || 4)),
          points: cleanPoints(m.points),
          text: m.kind === 'text' ? String(m.text ?? '').slice(0, MAX_TEXT_LENGTH) : undefined,
          done,
          doneAt: done ? now : undefined,
        })
        return true
      }
      case 'append': {
        const m = this.marks.get(msg.id)
        if (!m) return false
        m.points.push(...cleanPoints(msg.points))
        if (msg.done && !m.done) { m.done = true; m.doneAt = now }
        return true
      }
      case 'pointer':
        this.pointer = { x: clamp01(msg.x), y: clamp01(msg.y), color: MARK_COLORS.includes(msg.color as typeof MARK_COLORS[number]) ? msg.color : MARK_COLORS[0], at: now }
        return true
      case 'undo':
        return this.marks.delete(msg.id)
      case 'clear':
        this.marks.clear()
        this.pointer = null
        return true
      default:
        return false
    }
  }

  /** Tira o que já sumiu. Devolve se ainda há algo pra animar (lápis/seta com prazo, ponteiro). */
  prune(now = Date.now()): boolean {
    for (const [id, m] of this.marks) {
      if (m.kind !== 'text' && m.done && m.doneAt !== undefined && now - m.doneAt > STROKE_TTL_MS) this.marks.delete(id)
    }
    if (this.pointer && now - this.pointer.at > POINTER_TTL_MS) this.pointer = null
    return !!this.pointer || [...this.marks.values()].some(m => m.kind !== 'text')
  }

  get isEmpty() { return !this.marks.size && !this.pointer }
}

function alphaFor(m: Mark, now: number): number {
  if (m.kind === 'text' || !m.done || m.doneAt === undefined) return 1
  const left = STROKE_TTL_MS - (now - m.doneAt)
  return left >= FADE_MS ? 1 : Math.max(0, left / FADE_MS)
}

/** Desenha tudo num canvas do tamanho (CSS px) w × h. */
export function drawMarks(ctx: CanvasRenderingContext2D, w: number, h: number, board: MarkBoard, now = Date.now()) {
  ctx.clearRect(0, 0, w, h)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  for (const m of board.marks.values()) {
    ctx.globalAlpha = alphaFor(m, now)
    const pts = m.points.map(([x, y]) => [x * w, y * h] as Point)
    if (!pts.length) continue
    ctx.strokeStyle = m.color
    ctx.fillStyle = m.color
    ctx.lineWidth = m.width

    if (m.kind === 'stroke') {
      ctx.beginPath()
      ctx.moveTo(pts[0][0], pts[0][1])
      for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y)
      if (pts.length === 1) ctx.lineTo(pts[0][0] + 0.1, pts[0][1])
      ctx.stroke()
    } else if (m.kind === 'arrow' && pts.length >= 2) {
      const [a, b] = [pts[0], pts[pts.length - 1]]
      const angle = Math.atan2(b[1] - a[1], b[0] - a[0])
      const head = 10 + m.width * 2
      ctx.beginPath()
      ctx.moveTo(a[0], a[1])
      ctx.lineTo(b[0], b[1])
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(b[0], b[1])
      ctx.lineTo(b[0] - head * Math.cos(angle - Math.PI / 7), b[1] - head * Math.sin(angle - Math.PI / 7))
      ctx.lineTo(b[0] - head * Math.cos(angle + Math.PI / 7), b[1] - head * Math.sin(angle + Math.PI / 7))
      ctx.closePath()
      ctx.fill()
    } else if (m.kind === 'text' && m.text) {
      drawBubble(ctx, pts[0][0], pts[0][1], m.text, m.color, w)
    }
  }

  const p = board.pointer
  if (p) {
    const fade = Math.max(0, 1 - Math.max(0, now - p.at - (POINTER_TTL_MS - 500)) / 500)
    ctx.globalAlpha = 0.35 * fade
    ctx.fillStyle = p.color
    ctx.beginPath()
    ctx.arc(p.x * w, p.y * h, 18, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = fade
    ctx.beginPath()
    ctx.arc(p.x * w, p.y * h, 6, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.globalAlpha = 1
}

function drawBubble(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, color: string, canvasWidth: number) {
  ctx.font = '600 14px Poppins, system-ui, sans-serif'
  const maxWidth = 260
  const lines: string[] = []
  for (const paragraph of text.split('\n')) {
    let line = ''
    for (const word of paragraph.split(/\s+/)) {
      const next = line ? `${line} ${word}` : word
      if (ctx.measureText(next).width > maxWidth && line) { lines.push(line); line = word } else line = next
    }
    lines.push(line)
  }
  const pad = 8
  const lineH = 19
  const bw = Math.min(maxWidth, Math.max(...lines.map(l => ctx.measureText(l).width))) + pad * 2
  const bh = lines.length * lineH + pad * 2 - 4
  const bx = Math.min(Math.max(0, x), Math.max(0, canvasWidth - bw))
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.roundRect(bx, y, bw, bh, 8)
  ctx.fill()
  ctx.fillStyle = '#fff'
  lines.forEach((l, i) => ctx.fillText(l, bx + pad, y + pad + 13 + i * lineH))
}

/** Canvas nítido em tela de alta densidade: ajusta o tamanho interno ao CSS × devicePixelRatio. */
export function fitCanvas(canvas: HTMLCanvasElement, w: number, h: number): CanvasRenderingContext2D | null {
  const dpr = window.devicePixelRatio || 1
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
  }
  const ctx = canvas.getContext('2d')
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0)
  return ctx
}

export const newMarkId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).slice(0, 36)
